import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, HeartIcon, MapPinIcon, MountainsIcon, StarIcon, UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AppShell, Surface } from "@/components/layout/AppShell";
import { MapView } from "@/components/MapView";
import { ParkCard } from "@/components/ParkCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { parkPath } from "@/lib/routes";
import {
  PERSONAL_MAP_FILTERS,
  filterPersonalMapEntries,
  privateStateBadges,
  updateEntryState,
} from "@/lib/personalMap";

const STATE_ACTIONS = [
  { id: "want_to_ride", label: "Want to Ride", icon: HeartIcon },
  { id: "visited", label: "Visited", icon: CheckCircleIcon },
  { id: "favorite", label: "Favorite", icon: StarIcon },
];

function StateControls({ destinationId, state, busyField, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2 border-t border-white/5 px-4 py-3" data-testid={`personal-state-controls-${destinationId}`}>
      {STATE_ACTIONS.map(({ id, label, icon: Icon }) => {
        const active = Boolean(state[id]);
        return (
          <button
            key={id}
            type="button"
            disabled={Boolean(busyField)}
            onClick={() => onToggle(id)}
            data-testid={`personal-state-${id}-${destinationId}`}
            aria-pressed={active}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
              active
                ? "border-[#CCFF00]/50 bg-[#CCFF00]/10 text-[#CCFF00]"
                : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
            }`}
          >
            <Icon size={14} weight={active ? "fill" : "regular"} />
            {translateLabel(label)}
          </button>
        );
      })}
    </div>
  );
}

function PersonalDestinationCard({ entry, active, busyField, onSelect, onHover, onStateToggle }) {
  const badges = privateStateBadges(entry.state);
  return (
    <div className={`overflow-hidden rounded-2xl border bg-[#121214] ${active ? "border-[#CCFF00]" : "border-white/5"}`}>
      <div className="flex flex-wrap gap-1.5 px-4 pt-3" data-testid={`personal-state-badges-${entry.destination.id}`}>
        {badges.map((badge) => (
          <span key={badge.id} className="rounded-full border border-white/10 bg-black/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-zinc-300">
            {translateLabel(badge.label)}
          </span>
        ))}
      </div>
      <div className="[&_[data-testid=card-fav-btn]]:hidden">
        <ParkCard
          park={entry.destination}
          active={active}
          onHover={onHover}
          onSelect={onSelect}
          showFavorite={false}
        />
      </div>
      <StateControls
        destinationId={entry.destination.id}
        state={entry.state}
        busyField={busyField}
        onToggle={onStateToggle}
      />
    </div>
  );
}

export default function PersonalMap() {
  const navigate = useNavigate();
  const { user, loadFavorites } = useAuth();
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({ destinationId: null, field: null });

  const loadPersonalMap = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/personal-map");
      setEntries(data || []);
    } catch (err) {
      setEntries([]);
      setError(err.response?.data?.detail || err.message || "Could not load your Personal Map.");
    }
  }, []);

  useEffect(() => {
    if (user) loadPersonalMap();
    if (user === false) setEntries(null);
  }, [user, loadPersonalMap]);

  const visibleEntries = useMemo(
    () => filterPersonalMapEntries(entries || [], filter),
    [entries, filter],
  );
  const parks = useMemo(() => visibleEntries.map((entry) => entry.destination), [visibleEntries]);

  useEffect(() => {
    if (selectedId && !parks.some((park) => park.id === selectedId)) setSelectedId(null);
  }, [parks, selectedId]);

  const toggleState = async (entry, field) => {
    const destinationId = entry.destination.id;
    const nextValue = !entry.state[field];
    setBusy({ destinationId, field });
    try {
      const { data: nextState } = await api.post(`/destination-states/${destinationId}`, { [field]: nextValue });
      setEntries((current) => updateEntryState(current || [], destinationId, nextState));
      if (field === "want_to_ride") await loadFavorites();
      toast.success(nextValue ? `${STATE_ACTIONS.find((item) => item.id === field)?.label} added` : "Personal state updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not update your ride state.");
    } finally {
      setBusy({ destinationId: null, field: null });
    }
  };

  if (user === false) {
    return (
      <AppShell size="form">
          <Surface className="flex flex-col items-center px-6 py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#CCFF00] text-black">
              <UserIcon size={28} weight="bold" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-black uppercase text-white">{translateLabel("Your Personal Map")}</h1>
            <p className="mt-3 max-w-lg text-sm leading-6 text-zinc-400">
              Sign in to see only the destinations you marked as Want to Ride, Visited or Favorite. Public discovery stays available without an account.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate("/login")} className="rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black" data-testid="personal-map-sign-in">
                {translateLabel("Sign in")}
              </button>
              <button onClick={() => navigate("/")} className="rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-bold text-white" data-testid="personal-map-discover">
                Continue discovery
              </button>
            </div>
          </Surface>
      </AppShell>
    );
  }

  return (
    <AppShell size="canvas" contentClassName="pb-12">
        <header className="flex flex-col gap-5 py-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">
              <MapPinIcon size={15} weight="fill" /> Private rider state
            </div>
            <h1 className="mt-2 font-display text-4xl font-black uppercase text-white md:text-5xl">{translateLabel("Personal Map")}</h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-400">{translateLabel("Your private ride shortlist on top of canonical destination data.")}</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="personal-map-filters">
            {PERSONAL_MAP_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setFilter(item.id)}
                aria-pressed={filter === item.id}
                data-testid={`personal-map-filter-${item.id}`}
                className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                  filter === item.id
                    ? "border-[#CCFF00] bg-[#CCFF00] text-black"
                    : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
                }`}
              >
                {translateLabel(item.label)}
              </button>
            ))}
          </div>
        </header>

        {user === null || entries === null ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,520px)_1fr]">
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-72 rounded-2xl bg-white/5" />)}
            </div>
            <Skeleton className="h-[65vh] rounded-3xl bg-white/5" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center rounded-3xl border border-red-500/20 bg-red-500/5 px-6 py-20 text-center">
            <p className="font-display text-2xl font-bold text-white">{translateLabel("Could not load your Personal Map")}</p>
            <p className="mt-2 max-w-lg text-sm text-zinc-400">{error}</p>
            <button onClick={loadPersonalMap} className="mt-6 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-black" data-testid="personal-map-retry">{translateLabel("Try again")}</button>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center rounded-3xl border border-white/10 bg-[#121214] px-6 py-24 text-center" data-testid="personal-map-empty">
            <MountainsIcon size={52} className="text-zinc-600" />
            <p className="mt-5 font-display text-2xl font-bold text-white">{translateLabel("Your map is empty")}</p>
            <p className="mt-2 max-w-md text-sm text-zinc-400">{translateLabel("Mark a destination as Want to Ride, Visited or Favorite and it will appear here.")}</p>
            <button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black">{translateLabel("Discover rides")}</button>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(360px,520px)_1fr]">
            <section className="order-2 space-y-4 lg:order-1" data-testid="personal-map-list">
              <div className="flex items-center justify-between px-1 text-xs font-semibold text-zinc-500">
                <span>{visibleEntries.length} destination{visibleEntries.length === 1 ? "" : "s"}</span>
                <span>{translateLabel(PERSONAL_MAP_FILTERS.find((item) => item.id === filter)?.label)}</span>
              </div>
              {visibleEntries.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-[#121214] px-5 py-12 text-center text-sm text-zinc-400" data-testid="personal-map-filter-empty">
                  {translateLabel("No destinations match this private-state filter.")}
                </div>
              ) : visibleEntries.map((entry) => (
                <PersonalDestinationCard
                  key={entry.destination.id}
                  entry={entry}
                  active={selectedId === entry.destination.id}
                  busyField={busy.destinationId === entry.destination.id ? busy.field : null}
                  onHover={setHoveredId}
                  onSelect={() => navigate(parkPath(entry.destination))}
                  onStateToggle={(field) => toggleState(entry, field)}
                />
              ))}
            </section>

            <section className="order-1 h-[52vh] overflow-hidden rounded-3xl border border-white/10 bg-[#121214] lg:sticky lg:top-24 lg:order-2 lg:h-[calc(100vh-7rem)]" data-testid="personal-map-map">
              {parks.length > 0 ? (
                <MapView
                  parks={parks}
                  selectedId={selectedId}
                  hoveredId={hoveredId}
                  onSelect={setSelectedId}
                  onHover={setHoveredId}
                  fitKey={`${filter}:${parks.map((park) => park.id).join(",")}`}
                  cluster={parks.length > 20}
                />
              ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-zinc-500">{translateLabel("No map markers for this filter.")}</div>
              )}
            </section>
          </div>
        )}
    </AppShell>
  );
}
